import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { PresenceStatus } from './useGetMentionableUsers';

export type AwayPreset = '30m' | '1h' | '2h' | 'today' | 'this_week';

export interface UpdateUserStatusInput {
    presenceStatus: PresenceStatus;
    statusNote?: string;
    preset?: AwayPreset;
    timezoneOffsetMinutes?: number;
    awayStartsAt?: string;
    awayEndsAt?: string;
}

export interface UserStatusResponse {
    presenceStatus: PresenceStatus;
    statusNote: string | null;
    awayStartsAt: string | null;
    awayEndsAt: string | null;
    lastActive: string | null;
    mayNotRespond: boolean;
    mayNotRespondReason: 'away' | 'inactive' | null;
}

const updateUserStatus = async (
    sessionId: string,
    input: UpdateUserStatusInput,
): Promise<UserStatusResponse> => {
    const { data } = await axios.post(`${API_URL}/profile/status`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

export const useUpdateUserStatus = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: UpdateUserStatusInput) =>
            updateUserStatus(sessionContext?.sessionId ?? '', input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
            queryClient.invalidateQueries({ queryKey: ['/order/mentionable-users'] });
        },
    });
};
